import type { HeadlineResponse } from '@on3/api';
import { Link } from '@on3/ui-lib/components/Link';
import { toFriendlyDate } from '@on3/ui-lib/utils/date';
import { PremiumArticleLock } from '@public/components/Headlines/PremiumArticleLock';
import { ResizedImage } from '@public/components/Image/ResizedImage';
import clsx from 'clsx';
import { useMemo } from 'react';

import styles from './ArticleCover.module.scss';

interface IExtendedArticleProps {
  categoryColor?: string;
  titleColor?: string;
  authorColor?: string;
  mode?: 'dark' | 'light';
  page?: 'network' | 'channel' | 'team';
  index?: number;
  item?: HeadlineResponse;
}

export const ArticleCover = ({
  index,
  categoryColor,
  titleColor,
  authorColor,
  item,
}: IExtendedArticleProps) => {
  const {
    featuredImage,
    title,
    fullUrl,
    author,
    postDate,
    primaryCategory,
    isPremium,
  } = item || {};

  let baseUrl = primaryCategory?.fullUrl;

  if (baseUrl?.endsWith('/')) {
    baseUrl = baseUrl.slice(0, -1);
  }

  const categoryColorStyle = useMemo(() => {
    return { color: categoryColor };
  }, [categoryColor]);
  const titleColorStyle = useMemo(() => {
    return { color: titleColor };
  }, [titleColor]);
  const authorColorStyle = useMemo(() => {
    return { color: authorColor };
  }, [authorColor]);

  return (
    <article>
      <style>{`:root { --category-color: ${categoryColor}; }`}</style>
      {featuredImage && (
        <Link
          className={styles.imageLink}
          data-article-ui="image"
          href={fullUrl || '/'}
        >
          <ResizedImage
            className={clsx(styles.image, {
              [styles.imageSecondary]: index,
            })}
            {...featuredImage}
            fit="cover"
            gravity="0.5x0.3"
            height={223}
            loading="eager"
            quality={90}
            width={450}
          />
        </Link>
      )}
      <div className={styles.content}>
        <Link
          className={styles.category}
          href={`${baseUrl}/news/`}
          style={categoryColorStyle}
        >
          {primaryCategory?.name}
        </Link>
        <Link href={fullUrl || '/'} style={titleColorStyle}>
          <h2 className={styles.title}>{title}</h2>
        </Link>
        <div className={styles.authorBlock} style={authorColorStyle}>
          <Link className={styles.author} href={`/user/${author?.niceName}`}>
            {author?.name}
          </Link>
          <span className={styles.time}>
            <span className={styles.bullet}>&#8226;</span>
            {toFriendlyDate(postDate)}
          </span>
          {isPremium && <PremiumArticleLock />}
        </div>
      </div>
    </article>
  );
};
